/**
 * Owner Center (/owner) forms. Every form emails its answers to support@gritvuegear.com
 * through FormSubmit, the same service already confirmed for /activate.
 * Each form has its own email subject so each type is easy to find in the inbox.
 */
export const OWNER_FORM_ENDPOINT = 'https://formsubmit.co/ajax/support@gritvuegear.com'

export type OwnerFormId = 'warranty' | 'return' | 'rewards' | 'support'

export type OwnerField =
  | { kind: 'text' | 'email' | 'tel'; name: string; label: string; placeholder?: string; required?: boolean; autoComplete?: string; half?: boolean }
  | { kind: 'textarea'; name: string; label: string; placeholder?: string; required?: boolean }
  | { kind: 'chips'; name: string; label: string; options: string[]; required?: boolean }

export interface OwnerFormConfig {
  id: OwnerFormId
  title: string
  intro: string
  subject: string
  submitLabel: string
  successTitle: string
  successBody: string
  note?: string
  fields: OwnerField[]
}

export const ownerForms: Record<OwnerFormId, OwnerFormConfig> = {
  warranty: {
    id: 'warranty',
    title: 'Replacement claim',
    intro: 'Broke it? Tell us what happened and we get a replacement moving.',
    subject: 'Replacement claim',
    submitLabel: 'Send my claim',
    successTitle: 'Claim received.',
    successBody:
      'We reply within one business day. Reply to that email with a photo of the damage so we can approve it fast.',
    note: 'Coverage depends on your order: 30 days on the Essential, 1 year on single Complete Systems, Lifetime on multi-packs or when added. A $6.95 shipping and handling fee applies per approved claim. Terms apply.',
    fields: [
      { kind: 'text', name: 'Order number', label: 'Order number', placeholder: '#1001', required: true, half: true },
      { kind: 'email', name: 'Email', label: 'Email', placeholder: 'you@email.com', required: true, autoComplete: 'email', half: true },
      { kind: 'text', name: 'Full name', label: 'Full name', required: true, autoComplete: 'name', half: true },
      { kind: 'tel', name: 'Phone', label: 'Phone (optional)', autoComplete: 'tel', half: true },
      { kind: 'chips', name: 'Coverage', label: 'Your coverage', options: ['Lifetime', '1-Year', '30-Day (Essential)', 'Not sure'], required: true },
      { kind: 'chips', name: 'What needs replacing', label: 'What needs replacing?', options: ['X1 frame', 'Clear lens', 'Amber lens', 'Smoke lens', 'Hard case', 'Other'], required: true },
      { kind: 'textarea', name: 'What happened', label: 'What happened?', placeholder: 'Dropped it off a ladder, the lens cracked...', required: true },
      { kind: 'textarea', name: 'Shipping address', label: 'Shipping address for the replacement', placeholder: 'Street, city, state, ZIP', required: true },
    ],
  },
  return: {
    id: 'return',
    title: 'Return request',
    intro: 'Not for you? You have 30 days from delivery to send it back.',
    subject: 'Return request',
    submitLabel: 'Request my return',
    successTitle: 'Return request received.',
    successBody: 'We email your GritVue return label, usually within one business day. Use only that label to ship your return.',
    note: 'Changed your mind? We send you a GritVue USPS return label. Returns are accepted only with that label, no exceptions. The item must arrive unused and complete. We refund the purchase price less a 15% restocking fee and a $10 return shipping fee, within 2 business days after it reaches us and passes inspection. Arrived defective, damaged or wrong? The return is free, with no fees.',
    fields: [
      { kind: 'text', name: 'Order number', label: 'Order number', placeholder: '#1001', required: true, half: true },
      { kind: 'email', name: 'Email', label: 'Email', placeholder: 'you@email.com', required: true, autoComplete: 'email', half: true },
      { kind: 'text', name: 'Full name', label: 'Full name', required: true, autoComplete: 'name' },
      { kind: 'chips', name: 'What are you returning', label: 'What are you returning?', options: ['Whole order', 'Part of my order'], required: true },
      { kind: 'chips', name: 'Reason', label: 'Reason', options: ['Fit', 'Brightness', 'Not what I expected', 'Ordered by mistake', 'Arrived damaged', 'Other'], required: true },
      { kind: 'textarea', name: 'Details', label: 'Anything else we should know? (optional)' },
    ],
  },
  rewards: {
    id: 'rewards',
    title: 'Join Crew Rewards',
    intro: 'Share GritVue. Get paid. Your friend gets 10% off (up to $10), you earn $15 per qualified order.',
    subject: 'Crew Rewards sign-up',
    submitLabel: 'Sign me up',
    successTitle: "You're on the list.",
    successBody: 'We email your personal referral link as soon as your account is set up.',
    note: 'Rewards are paid on qualified, completed orders after the return window. No self-referrals. Program terms apply.',
    fields: [
      { kind: 'text', name: 'Full name', label: 'Full name', required: true, autoComplete: 'name', half: true },
      { kind: 'email', name: 'Email', label: 'Email', placeholder: 'you@email.com', required: true, autoComplete: 'email', half: true },
      { kind: 'text', name: 'Order number', label: 'Order number (if you own an X1)', placeholder: '#1001', half: true },
      { kind: 'text', name: 'Trade', label: 'Your trade', placeholder: 'Mechanic, electrician...', half: true },
      { kind: 'chips', name: 'Where you would share', label: 'Where would you share it?', options: ['Coworkers / crew', 'Facebook / Instagram', 'TikTok', 'YouTube', 'Text / WhatsApp', 'Other'] },
      { kind: 'chips', name: 'Crew size', label: 'How many people on your crew?', options: ['Just me', '2–5', '6–20', '20+'] },
    ],
  },
  support: {
    id: 'support',
    title: 'Contact support',
    intro: 'Questions about your X1, your order or RX? A real person answers, usually within one business day.',
    subject: 'Support request',
    submitLabel: 'Send message',
    successTitle: 'Message sent.',
    successBody: 'We reply to your email, usually within one business day (Monday to Friday, 8am–6pm CT).',
    fields: [
      { kind: 'text', name: 'Full name', label: 'Full name', required: true, autoComplete: 'name', half: true },
      { kind: 'email', name: 'Email', label: 'Email', placeholder: 'you@email.com', required: true, autoComplete: 'email', half: true },
      { kind: 'text', name: 'Order number', label: 'Order number (optional)', placeholder: '#1001' },
      { kind: 'chips', name: 'Topic', label: 'Topic', options: ['My order', 'Using my X1', 'RX insert', 'Crew / business order', 'Other'], required: true },
      { kind: 'textarea', name: 'Message', label: 'Message', required: true },
    ],
  },
}
